/* eslint-disable react/jsx-curly-newline */
import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  ButtonWithMenu,
  Icon,
  MenuItem,
  MenuItemText,
  Tooltip,
  Typography,
} from '@statsbomb/kitbag-components';
import { has } from 'lodash';
import { useKitbagAuth } from '@statsbomb/kitbag-auth';
import {
  ScoutTemplateActionButtons,
  ScoutTemplatesHeader,
  ScoutTemplateTitle,
} from './ScoutTemplateManagement.styles';
import LoadModal from '../LoadModal/LoadModal';
import { ButtonWithMenuWrapper } from '../../../../components/Tables/SortableTable/SortableTable.styles';
import ScoutConfirmModal from '../ScoutConfirmModal/ScoutConfirmModal';
import { SCOUT_TEMPLATE_MODES } from '../../Scout.constants';
import { hasUnsavedChanges as unsavedChanges } from '../ScoutResults.dataManipulation';
import {
  disableSaveTemplateState,
  isDefaultTemplateState,
} from './ScoutTemplateManagement.helpers';
import {
  trackObjectChangeEvent,
  trackObjectSaveEvent,
} from '../../../../utils/helpers/snowplow';
import { useAccountConfigContext } from '../../../../utils/providers/AccountConfigProvider';
import { useStore } from '../../../../store/useStore';

const ScoutTemplateManagement = ({
  __storybookScoutSetups = null,
  __storybookLoadModalOpen = null,
  __storybookHandleMenuAction = null,
}) => {
  const selectedStats = useStore((s) => s.selectedStats);
  const selectedPositions = useStore((s) => s.selectedPositions);
  const selectedBiometrics = useStore((s) => s.selectedBiometrics);
  const showFavourites = useStore((s) => s.showFavourites);
  const saveCurrentSetup = useStore((s) => s.saveCurrentSetup);
  const saveAsNewSetup = useStore((s) => s.saveAsNewSetup);
  const activeTemplateId = useStore((s) => s.activeTemplateId);
  const clearAll = useStore((s) => s.clearAll);
  const clearUnsaved = useStore((s) => s.clearUnsaved);
  const deleteTemplate = useStore((s) => s.deleteTemplate);
  const savedScoutTemplates = useStore((s) => s.savedScoutTemplates);
  const setTemplateName = useStore((s) => s.setTemplateName);

  const { user } = useKitbagAuth();
  const { add, remove } = useAccountConfigContext();
  const savedTemplate =
    savedScoutTemplates?.find((s) => s.id === activeTemplateId) || {};

  const isDefaultState = isDefaultTemplateState({
    activeTemplateId,
    selectedStats,
    selectedPositions,
    selectedBiometrics,
    showFavourites,
  });

  const hasUnsavedChanges =
    unsavedChanges(
      savedTemplate,
      activeTemplateId,
      selectedStats,
      selectedPositions,
      selectedBiometrics,
      showFavourites
    ) && !isDefaultState;

  // a template being interacted with via an inline menu
  const confirmModalRef = useRef();
  const [confirmMode, setConfirmMode] = useState(null);
  // inline menu
  const [openMenu, setOpenMenu] = useState(false);
  const [tableTemplateId, setTableTemplateId] = useState(null);

  const handleMenuAction = (action, data) => {
    // if no data, return
    // table data has different data structure, selectable ids
    const fromTable = has(data, 'name.label');
    if (fromTable) {
      setTableTemplateId(data.id.label);
    } else {
      setTableTemplateId(null);
    }
    switch (action) {
      case SCOUT_TEMPLATE_MODES.EDIT:
        setConfirmMode(SCOUT_TEMPLATE_MODES.EDIT);
        trackObjectChangeEvent(
          'RuleList',
          'Rename',
          fromTable ? data.name.label : data.name
        );
        setTemplateName(fromTable ? data.name.label : data.name);
        confirmModalRef.current.open();
        break;
      case SCOUT_TEMPLATE_MODES.DELETE:
        setConfirmMode(SCOUT_TEMPLATE_MODES.DELETE);
        trackObjectChangeEvent(
          'RuleList',
          'Delete',
          fromTable ? data.name.label : data.name
        );
        setTemplateName(fromTable ? data.name.label : data.name);
        confirmModalRef.current.open();
        break;
      case SCOUT_TEMPLATE_MODES.SAVE_AS:
        setConfirmMode(SCOUT_TEMPLATE_MODES.SAVE_AS);
        trackObjectSaveEvent('Organizer', 'Save As');
        setTemplateName(fromTable ? data.name.label : data.name);
        confirmModalRef.current.open();
        break;
      case SCOUT_TEMPLATE_MODES.QUICK:
        saveCurrentSetup({
          mode: SCOUT_TEMPLATE_MODES.QUICK,
          user,
          add,
          id: fromTable ? data.id.label : savedTemplate?.id,
          openLoadModalAfter: tableTemplateId !== null,
        });
        trackObjectSaveEvent('Organizer', 'Save');
        break;
      default:
        break;
    }
  };

  // Storybook specific: handleMenuAction on component load
  useEffect(() => {
    if (typeof __storybookHandleMenuAction === 'function') {
      __storybookHandleMenuAction(handleMenuAction);
    }
  }, []);

  return (
    <>
      <ScoutTemplatesHeader>
        <div>
          <Typography variant="headingLarge">Filter</Typography>
          <ScoutTemplateActionButtons>
            <Button variant="ghost" size="small" onClick={clearAll}>
              Clear All
            </Button>
            <LoadModal
              modalId="loadModal"
              handleMenuAction={handleMenuAction}
              __storybookScoutSetups={__storybookScoutSetups}
              __storybookLoadModalOpen={__storybookLoadModalOpen}
            />
          </ScoutTemplateActionButtons>
        </div>
        <div>
          <ScoutTemplateTitle
            data-testid="filterSetTitle"
            $paddingLeft={hasUnsavedChanges ? '1.5rem' : '0'}
            $visible={hasUnsavedChanges}
            $opacity={activeTemplateId == null && hasUnsavedChanges ? 0.7 : 1}
          >
            <Tooltip
              content={<div style={{ width: '110px' }}>Unsaved changes</div>}
              placement="bottom-start"
            >
              <Icon variant="Warning" size="small" testId="unsaved-changes" />
            </Tooltip>
            <Tooltip
              content={savedTemplate?.name || 'Unsaved filters'}
              placement="bottom-start"
            >
              {savedTemplate?.name || 'Unsaved filters'}
            </Tooltip>
          </ScoutTemplateTitle>
          <ScoutTemplateActionButtons>
            <Button
              variant="primary"
              size="small"
              isDisabled={disableSaveTemplateState(
                hasUnsavedChanges,
                activeTemplateId,
                savedTemplate?.creatorId,
                user?.sub
              )}
              onClick={() => {
                handleMenuAction(
                  activeTemplateId != null
                    ? SCOUT_TEMPLATE_MODES.QUICK
                    : SCOUT_TEMPLATE_MODES.SAVE_AS,
                  savedTemplate
                );
              }}
            >
              Save
            </Button>
            <ButtonWithMenu
              icon="More"
              onClick={(event) => {
                event.stopPropagation();
                setOpenMenu(!openMenu);
              }}
              onOutsideClick={(event) => {
                event.stopPropagation();
                setOpenMenu(false);
              }}
              placement="bottom-end"
              variant="ghost"
              size="small"
              isOpen={openMenu}
              title="more-button"
            >
              <ButtonWithMenuWrapper>
                <MenuItem onClick={clearUnsaved} disabled={!hasUnsavedChanges}>
                  <MenuItemText>Discard Changes</MenuItemText>
                  <Icon variant="Refresh" size="small" />
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMenuAction(SCOUT_TEMPLATE_MODES.EDIT, savedTemplate);
                  }}
                  disabled={
                    (activeTemplateId == null && hasUnsavedChanges) ||
                    savedTemplate?.creatorId !== user?.sub
                  }
                >
                  <MenuItemText>Rename</MenuItemText>
                  <Icon variant="Edit" size="small" />
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMenuAction(
                      SCOUT_TEMPLATE_MODES.DELETE,
                      savedTemplate
                    );
                  }}
                  disabled={
                    (activeTemplateId == null && hasUnsavedChanges) ||
                    savedTemplate?.creatorId !== user?.sub
                  }
                  testId="delete"
                >
                  <MenuItemText>Delete</MenuItemText>
                  <Icon variant="Delete" size="small" />
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMenuAction(
                      SCOUT_TEMPLATE_MODES.SAVE_AS,
                      savedTemplate
                    );
                  }}
                  testId="save-as-new"
                >
                  <MenuItemText>Save as New</MenuItemText>
                  <Icon variant="SaveAs" size="small" />
                </MenuItem>
              </ButtonWithMenuWrapper>
            </ButtonWithMenu>
          </ScoutTemplateActionButtons>
        </div>
      </ScoutTemplatesHeader>

      <ScoutConfirmModal
        ref={confirmModalRef}
        mode={confirmMode}
        saveFunction={() =>
          saveCurrentSetup({
            user,
            add,
            id: tableTemplateId ?? savedTemplate?.id,
            openLoadModalAfter: tableTemplateId !== null,
          })
        }
        deleteFunction={() =>
          deleteTemplate({
            id: tableTemplateId ?? savedTemplate?.id,
            remove,
            openLoadModalAfter: tableTemplateId !== null,
          })
        }
        saveAsNewFunction={() =>
          saveAsNewSetup({
            user,
            add,
            id: tableTemplateId,
            openLoadModalAfter: tableTemplateId !== null,
          })
        }
      />
    </>
  );
};
export default ScoutTemplateManagement;

ScoutTemplateManagement.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  __storybookScoutSetups: PropTypes.arrayOf(PropTypes.object),
  __storybookLoadModalOpen: PropTypes.bool,
  __storybookHandleMenuAction: PropTypes.func,
};
